const MAX_WIDTH = 1600;
const TARGET_SIZE = 400 * 1024; // 400 KB
const INITIAL_QUALITY = 0.82;
const MIN_QUALITY = 0.4;
const QUALITY_STEP = 0.08;

/**
 * Optimizes an image file: resizes to max 1600px width, converts to WebP,
 * and iteratively compresses to target <400 KB.
 */
export async function optimizeImage(file: File): Promise<File> {
  const bitmap = await createImageBitmap(file);
  
  let width = bitmap.width;
  let height = bitmap.height;

  if (width > MAX_WIDTH) {
    height = Math.round((height * MAX_WIDTH) / width);
    width = MAX_WIDTH;
  }

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas context unavailable");

  ctx.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();

  // Check WebP support
  const supportsWebP = canvas.toDataURL("image/webp").startsWith("data:image/webp");
  const mimeType = supportsWebP ? "image/webp" : "image/jpeg";
  const extension = supportsWebP ? "webp" : "jpg";

  let quality = INITIAL_QUALITY;
  let blob: Blob | null = null;

  while (quality >= MIN_QUALITY) {
    blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, mimeType, quality)
    );
    if (blob && blob.size <= TARGET_SIZE) break;
    quality -= QUALITY_STEP;
  }

  if (!blob) throw new Error("Image optimization failed");

  const optimizedName = file.name.replace(/\.[^.]+$/, `.${extension}`);
  return new File([blob], optimizedName, { type: mimeType });
}
